// src/pages/YourRiders.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO, isAfter, isToday } from "date-fns";
import { MessageCircle, CheckCircle } from "lucide-react";
import socket from "../socket";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");
const API_RIDEPAY = `${API_BASE}/api/ridepayment`;

const YourRiders = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  const [bookings, setBookings] = useState([]);
  const [rideDetails, setRideDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState({
    open: false,
    booking: null,
  });

  const getRawStatus = (b) =>
    b.requestStatus || b.rideStatus || b.status || "accepted";

  const getStatusLabel = (raw) => {
    if (raw === "worker_completed") return "Awaiting customer";
    if (raw === "completed") return "Completed";
    if (raw === "refunded") return "Refunded";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const getFullPhotoUrl = (raw) => {
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    return `${API_BASE}${raw.startsWith("/") ? raw : "/" + raw}`;
  };

  const safeFormatDateTime = (dateString, fallback = "N/A") => {
    if (!dateString) return fallback;
    try {
      return format(parseISO(dateString), "MMMM d, yyyy");
    } catch {
      return fallback;
    }
  };

  /* =====================================
      FETCH CONFIRMED RIDERS
  ====================================== */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reqRes, rideRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ride/${rideId}/requests-and-chats`),
          axios.get(`${API_BASE}/api/ride/preview/${rideId}`),
        ]);

        const all = reqRes.data.requests || [];

        // Only riders who are IN the trip with driver
        const confirmed = all.filter((b) => {
          const s = getRawStatus(b);
          return ["accepted", "worker_completed", "completed", "refunded"].includes(
            s
          );
        });

        setBookings(
          confirmed.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() -
              new Date(b.createdAt).getTime()
          )
        );
        setRideDetails(rideRes.data);
      } catch (err) {
        console.error("❌ Failed to load riders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    socket.emit("join-ride", rideId);

    socket.on("ride-status-update", (update) => {
      if (String(update.rideId) !== String(rideId)) return;

      setBookings((prev) => {
        const updated = prev.map((b) =>
          String(b._id) === String(update.requestId)
            ? { ...b, requestStatus: update.status }
            : b
        );

        // if something is no longer a confirmed status, drop it
        return updated.filter((b) =>
          ["accepted", "worker_completed", "completed", "refunded"].includes(
            getRawStatus(b)
          )
        );
      });
    });

    return () => {
      socket.off("ride-status-update");
    };
  }, [rideId]);

  /* =====================================
      MARK BOOKING COMPLETE (worker side)
  ====================================== */
  const handleConfirmComplete = async () => {
    if (!modal.booking) return;
    const booking = modal.booking;

    try {
      await axios.post(`${API_RIDEPAY}/worker-complete`, {
        bookingId: booking._id,
        workerId,
      });

      socket.emit("ride-status-update", {
        rideId,
        requestId: booking._id,
        status: "worker_completed",
      });

      setBookings((prev) =>
        prev.map((b) =>
          String(b._id) === String(booking._id)
            ? {
                ...b,
                requestStatus: "worker_completed",
                driverComplete: true,
              }
            : b
        )
      );

      setModal({ open: false, booking: null });
    } catch (err) {
      console.error("❌ Worker-complete error:", err);
      alert("Failed to mark this rider as completed.");
    }
  };

  const openModal = (booking) => setModal({ open: true, booking });
  const closeModal = () => setModal({ open: false, booking: null });

  if (loading) return <div className="p-6">Loading riders...</div>;

  const today = new Date();

  return (
    <>
      <div className="p-6 max-w-6xl mx-auto font-[Poppins]">
        <h2 className="text-4xl font-extrabold mb-10 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 drop-shadow-md">
          Your Riders
        </h2>

        {rideDetails && (
          <div className="backdrop-blur-lg bg-white/70 shadow-xl rounded-2xl p-6 mb-10 border border-white/30">
            <p>
              <strong>Route:</strong> {rideDetails.from} → {rideDetails.to}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {safeFormatDateTime(rideDetails.date)} at {rideDetails.time}
            </p>
            <p>
              <strong>Seats Left:</strong> {rideDetails.seatsAvailable}
            </p>
          </div>
        )}

        {bookings.length === 0 ? (
          <p className="text-gray-600">
            No confirmed riders yet. Once you accept a request, the passenger
            will appear here.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-8 mb-14">
            {bookings.map((b) => {
              const rawStatus = getRawStatus(b);
              const statusLabel = getStatusLabel(rawStatus);
              const rideObj = b.rideId || {};
              const rideDate = rideObj.date ? parseISO(rideObj.date) : null;
              const ridePassed =
                rideDate &&
                (isToday(rideDate) || isAfter(today, rideDate));

              const canWorkerComplete =
                rawStatus === "accepted" && ridePassed;

              const c = b.customerId || {};
              const photoUrl = getFullPhotoUrl(c.profilePhotoUrl);

              return (
                <div
                  key={b._id}
                  className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 rounded-3xl shadow-2xl border border-indigo-100 hover:shadow-blue-200 hover:scale-[1.02] transition"
                >
                  {/* Status badge */}
                  <div className="absolute top-4 right-4 text-xs px-3 py-1 rounded-full bg-white/80 shadow font-semibold text-gray-700">
                    {statusLabel}
                  </div>

                  {/* Passenger Info */}
                  <div className="flex items-center gap-4 mb-4">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={c.name}
                        className="w-14 h-14 rounded-full border-2 border-blue-200 shadow"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center font-bold text-lg">
                        {(c.name || "P")[0].toUpperCase()}
                      </div>
                    )}

                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        {c.name}
                      </p>
                      {b.message && (
                        <p className="italic text-gray-600">{b.message}</p>
                      )}
                      {rideDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Ride on {format(rideDate, "MMMM d, yyyy")} at{" "}
                          {rideObj.time || "TBD"}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Seats booked: {b.seats || 1} • Paid: $
                        {typeof b.finalPrice === "number"
                          ? b.finalPrice.toFixed(2)
                          : b.finalPrice || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Chat shortcut */}
                  <button
                    onClick={() =>
                      navigate(`/worker/ride-chat/${rideId}/${c._id}`)
                    }
                    className="absolute bottom-4 right-4 p-3 rounded-full bg-white shadow hover:bg-blue-50 transition"
                  >
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </button>

                  {/* Buttons */}
                  <div className="flex flex-col gap-3 mt-3">
                    {canWorkerComplete && (
                      <button
                        onClick={() => openModal(b)}
                        className="flex items-center gap-2 px-5 py-2 rounded-full bg-green-50 border border-green-300 text-green-700 font-semibold hover:bg-green-100"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark Rider Complete
                      </button>
                    )}

                    {rawStatus === "worker_completed" && (
                      <div className="mt-2 px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 font-semibold text-center shadow">
                        Waiting for customer confirmation
                      </div>
                    )}

                    {rawStatus === "completed" && (
                      <div className="mt-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-center shadow">
                        Ride completed ✔
                      </div>
                    )}

                    {rawStatus === "refunded" && (
                      <div className="mt-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-semibold text-center shadow">
                        Refunded
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CONFIRM MODAL */}
      {modal.open && modal.booking && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-900 text-center">
              Mark rider as completed?
            </h3>
            <p className="text-sm text-gray-600 mb-6 text-center">
              This tells the system that this passenger finished the trip. We
              will then wait for the passenger to confirm on their side before
              releasing the payout.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmComplete}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default YourRiders;
